import { copy } from 'fast-copy'

/**
 * Creates a tree-like structure where "id" and "title" are required.
 * Child and parent nodes can be extended with "L" and "P" respectively.
 */
export type NestedItem<L = object, P = object> = NestedLeaf<L> | NestedBranch<L, P>
export type NestedLeaf<L = object> = L & {
  id: string // unique throughout tree
  title: string // i18n key
  disableDrag?: boolean
}
export type NestedBranch<L = object, P = object> = P & {
  id: string // unique throughout tree
  title: string // i18n key
  disableDrag?: boolean
  children: (NestedBranch<L, P> | NestedLeaf<L>)[]
}

type HasChildren<L = object, P = object> = P & { children: MaybeHasChildren<L, P>[] }
type MaybeHasChildren<L = object, P = object> = L | HasChildren<L, P>

export function isNestedBranch<T>(item: NestedItem<T>): item is NestedBranch<T> {
  return 'children' in item && item.children !== undefined
}

export function isLeaf<T>(item: NestedItem<T>): item is NestedLeaf<T> {
  return !isNestedBranch(item)
}

/**
 * Returns two maps:
 * - leafsParentMap: maps leafs to their parent
 * - parentsChildMap: maps parent to their children
 */
export function getNodesMap<L, P>(
  tree: NestedItem<L, P>[],
): {
  leafsParentMap: Map<string, NestedBranch<L, P> | null>
  parentsChildMap: Map<string, NestedItem<L, P>[]>
  // parentsAll: Map<string, NestedLeaf[]>
} {
  const leafsParentMap: Map<string, NestedBranch<L, P> | null> = new Map()
  const parentsChildMap: Map<string, NestedItem<L, P>[]> = new Map()
  // const parentsAll: Map<string, NestedLeaf[]> = new Map() // include all descendants

  for (const item of tree) {
    if (isNestedBranch(item)) {
      const parent = item
      parentsChildMap.set(parent.id, parent.children)
      // parentsAll.set(parent.id, parent.children)

      const nested = getNodesMap(parent.children)
      for (const [id, _] of nested.leafsParentMap) {
        leafsParentMap.set(id, parent)
      }

      for (const [id, children] of nested.parentsChildMap) {
        parentsChildMap.set(id, children)
        // parentsAll.set(parent.id, (parentsAll.get(parent.id) ?? []).concat(children))
      }
    } else {
      leafsParentMap.set(item.id, null)
    }
  }
  return {
    leafsParentMap: leafsParentMap,
    parentsChildMap: parentsChildMap,
    // parentsAll
  }
}

export function getNodes<L, P>(tree: NestedItem<L, P>[]) {
  const nodes: (NestedLeaf<L> | NestedBranch<L, P>)[] = []
  for (const item of tree) {
    if (isNestedBranch(item)) {
      nodes.push(...getNodes(item.children))
    }
    nodes.push(item)
  }
  return nodes
}

/**
 * Recursively retrieves all leaf nodes from a nested parent object and returns them in a Map.
 */
export function getLeafsParent<T>(item: NestedBranch<T>): Map<string, NestedLeaf<T>> {
  const leafs: Map<string, NestedLeaf<T>> = new Map()
  for (const child of item.children) {
    if (isNestedBranch(child)) {
      for (const [id, nestedChild] of getLeafsParent(child)) {
        leafs.set(id, nestedChild)
      }
    } else {
      leafs.set(child.id, child)
    }
  }
  return leafs
}

/**
 * Recursively retrieves all leaf nodes from a nested parent
 */
export function getLeafsAll<T, P>(items: NestedItem<T, P>[]): NestedLeaf<T>[] {
  const leafs: NestedLeaf<T>[] = []
  walkNestedItems(items, (item) => {
    if (isLeaf(item)) {
      leafs.push(item)
    }
  })
  return leafs
}

export function getParentsLeafs<T, P>(
  items: NestedItem<T, P>[],
): {
  parents: NestedBranch<T, P>[]
  leafs: NestedLeaf<T>[]
} {
  const parents: NestedBranch<T, P>[] = []
  const leafs: NestedLeaf<T>[] = []
  walkNestedItems(items, (item) => {
    if (isNestedBranch(item)) {
      parents.push(item)
    } else {
      leafs.push(item)
    }
  })
  return {
    leafs,
    parents,
  }
}

/**
 * Merges children. "Source" values take precedence
 */
function mergeChildren(target: NestedItem, source?: NestedItem) {
  if (!source) {
    return target
  }
  // Assuming 'id' identifies whether two nodes should be merged.
  if (target.id !== source.id) {
    // Not the “same” node — consider them siblings
    return source
  }

  // Merge children recursively
  const childrenById: Record<string, NestedItem> = {}

  for (const child of isNestedBranch(source) ? source.children : []) {
    childrenById[child.id] = child
  }

  for (const childTarget of isNestedBranch(target) ? target.children : []) {
    childrenById[childTarget.id] = mergeChildren(childTarget, childrenById[childTarget.id])
  }

  const children = Object.values(childrenById)
  if (isNestedBranch(source)) {
    source.children = children
  }

  return source
}

/**
 * Merges items via id. "Source" list values take precedence
 */
export function mergeNestedItems(target: NestedItem[], source: NestedItem[]) {
  const map: Record<string, NestedItem> = {}
  for (const node of source) {
    map[node.id] = node
  }

  for (const node of target) {
    map[node.id] = mergeChildren(node, map[node.id])
  }

  return Object.values(map)
}

/**
 * Recursive remove items from the tree which should not be kept.
 *
 * Only looks for children ids (maybe we should also look for parent ids?)
 */
export function reduceTreeInplace(tree: NestedItem[], keepIds: string[]) {
  const retained = new Set<NestedItem>()
  const removed = new Set<NestedItem>()
  for (let i = 0; i < tree.length; i++) {
    const item = tree[i]

    let removeEntry = false
    if (isNestedBranch(item)) {
      // Dig into children
      reduceTreeInplace(item.children, keepIds)

      // Remove childless branch
      removeEntry = item.children.length === 0
    } else {
      // Remove leaf
      removeEntry = !keepIds.includes(item.id)
    }

    if (removeEntry) {
      tree.splice(i, 1)
      removed.add(item)
      i--
    } else {
      retained.add(item)
    }
  }
  return {
    retained,
    removed,
  }
}

export function removeNodes(tree: NestedItem[], idsToRemove: string[]) {
  const retained = new Set<NestedItem>()
  const removed = new Set<NestedItem>()
  for (let i = 0; i < tree.length; i++) {
    const item = tree[i]

    let removeEntry = false
    if (isNestedBranch(item)) {
      if (idsToRemove.includes(item.id)) {
        removeEntry = true
      } else {
        // Dig into children
        removeNodes(item.children, idsToRemove)

        // Remove childless branch
        removeEntry = item.children.length === 0
      }
    } else {
      // Remove leaf
      removeEntry = idsToRemove.includes(item.id)
    }

    if (removeEntry) {
      tree.splice(i, 1)
      removed.add(item)
      i--
    } else {
      retained.add(item)
    }
  }
  return {
    retained: Array.from(retained),
    removed: Array.from(removed),
  }
}

export function walkNestedItems<L extends object, P extends object, T extends MaybeHasChildren<L, P>>(
  items: T[],
  callback: (item: T) => void,
) {
  for (const item of items) {
    callback(item)
    if ('children' in item) {
      walkNestedItems(item.children as T[], callback)
    }
  }
}

/**
 * Creates a copy of the tree with filtered items removed.
 */
export function filterTree<T extends NestedItem>(tree: T[], filter: (item: T) => boolean, _copy = true): T[] {
  const filtered: T[] = []
  for (const item of _copy ? copy(tree) : tree) {
    if (filter(item)) {
      filtered.push(item)
      if (isNestedBranch(item)) {
        item.children = filterTree(item.children as T[], filter, false)
      }
    }
  }
  return filtered
}
